import React, { useRef, useState } from 'react';
import { AlertCircleIcon, CheckCircle2Icon, FileTextIcon, Trash2Icon, UploadIcon, StethoscopeIcon } from 'lucide-react';
interface PrescriptionUploadSidebarProps {
  onPrescriptionUpload?: (file: File) => void;
  onConsultDoctor?: () => void;
  compact?: boolean;
}
export function PrescriptionUploadSidebar({
  onPrescriptionUpload,
  onConsultDoctor,
  compact = false
}: PrescriptionUploadSidebarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please upload a valid file (JPG, PNG, or PDF)');
        return;
      }
      setUploadedFile(file);
      onPrescriptionUpload?.(file);
    }
  };
  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  const handleConsultClick = () => {
    onConsultDoctor?.();
  };
  if (compact) {
    return <div className="rounded-lg bg-white p-3 shadow-sm" data-id="element-4719" data-mp="26hvfpu3lpoyf">
        <div className="flex items-center justify-between gap-3" data-id="element-4720" data-mp="26f98xwojbdh9">
          <p className="text-sm font-bold text-gray-900" data-id="element-4721" data-mp="1sgj2hqfts1g8">Prescription</p>
          {!uploadedFile && <div className="flex items-center gap-2" data-id="element-4722" data-mp="1xfm5z6g5pzm3"><button type="button" onClick={handleUploadClick} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-brand px-3 text-xs font-bold text-white transition-colors duration-150 hover:bg-brand-dark" data-id="element-4723" data-mp="1xirvgmsp73ic"><UploadIcon className="h-4 w-4" data-id="element-4724" data-mp="lzfekv637g2a" /> Upload</button><button type="button" onClick={handleConsultClick} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-300 px-3 text-xs font-bold text-gray-800 transition-colors duration-150 hover:bg-gray-50" data-id="element-4725" data-mp="1znkdkl42tr9"><StethoscopeIcon className="h-4 w-4" data-id="element-4726" data-mp="3pb6o20wd6oem" /> Consult</button></div>}
        </div>
        {uploadedFile && <div className="mt-2 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-2" data-id="element-4727" data-mp="3kcll3tpq9qmh"><FileTextIcon className="h-4 w-4 shrink-0 text-emerald-700" data-id="element-4728" data-mp="3ffv85f97zb5a" /><p className="min-w-0 flex-1 truncate text-xs font-semibold text-emerald-900" data-id="element-4729" data-mp="3aepcb8k9j7rj">{uploadedFile.name}</p><CheckCircle2Icon className="h-4 w-4 shrink-0 text-emerald-600" aria-label="Uploaded" data-id="element-4730" data-mp="36cu2tgs6ho2o" /><button type="button" onClick={handleRemoveFile} className="rounded p-1 text-emerald-700 transition-colors duration-150 hover:bg-emerald-100 hover:text-red-600" aria-label="Remove uploaded prescription" data-id="element-4731" data-mp="31esnn6xvi8wh"><Trash2Icon className="h-4 w-4" data-id="element-4732" data-mp="zmmje1za0dsb" /></button></div>}
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,application/pdf" onChange={handleFileChange} className="hidden" data-id="element-4733" data-mp="zl31y81vvwoy" />
      </div>;
  }
  return <div className="bg-white rounded-lg shadow-sm p-4" data-id="element-4734" data-mp="1c471g2mccgy0">
      <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-4735" data-mp="1gvyjforflecj">Prescription</h3>
      {/* Warning Message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2" data-id="element-4736" data-mp="1lw34ls3aax93">
        <AlertCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-4737" data-mp="o8m7s9mt6qy0" />
        <p className="text-xs text-gray-700 leading-relaxed" data-id="element-4738" data-mp="t6nowp0ocg6u">
          To complete your order, please upload a valid prescription. Accepted
          formats: PDF or image (JPG/PNG).
        </p>
      </div>
      {/* Upload Button */}
      <button onClick={handleUploadClick} className="w-full bg-[#1E2B55] hover:bg-[#162041] text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors mb-2" data-id="element-4739" data-mp="128dh3x2drj5o">
        <UploadIcon className="w-5 h-5" data-id="element-4740" data-mp="2dkxtn9bksq7d" />
        Upload Prescription
      </button>
      {/* File Size Info */}
      <p className="text-xs text-gray-500 text-right mb-4" data-id="element-4741" data-mp="2c7od7z75dh42">
        Maximum file size: 5MB
      </p>
      {/* Divider Text */}
      <p className="text-sm text-gray-600 mb-3" data-id="element-4742" data-mp="2m3hz496ramyi">
        You can request a prescription if you do not have one
      </p>
      {/* Consult Doctor Button */}
      <button onClick={handleConsultClick} className="w-full bg-white hover:bg-gray-50 text-gray-900 font-semibold py-3 px-4 rounded-lg border-2 border-gray-300 flex items-center justify-center gap-2 transition-colors" data-id="element-4743" data-mp="2h3dfwbegre4l">
        <StethoscopeIcon className="w-5 h-5" data-id="element-4744" data-mp="34tzk1rlcke9b" />
        Request Prescription & Consult Doctor
      </button>
      {/* Hidden File Input */}
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,application/pdf" onChange={handleFileChange} className="hidden" data-id="element-4745" data-mp="2zwhdu8ypsd50" />
    </div>;
}